import React from 'react'
import {
  Tabs,
  TabsList,
  TabsTrigger,
  TabsContent,
} from '../../components/tabs'
import { UITypography } from '../display/ui-typography'
import { cn } from '../../lib/utils'
import { UIBox } from './ui-box'

export interface UITabItem {
  value: string
  label: string
  content: React.ReactNode
  disabled?: boolean
  icon?: React.ReactNode
}

export interface UITabsProps extends React.ComponentProps<typeof Tabs> {
  /**
   * 탭 아이템들
   */
  items?: UITabItem[]
  /**
   * 탭 크기
   */
  size?: 'sm' | 'default' | 'lg'
  /**
   * 탭 변형
   * - default: Apple 글래스모피즘 스타일 (권장)
   * - pills: 둥근 pill 형태
   * - line: 하단 라인 인디케이터 스타일
   */
  variant?: 'default' | 'pills' | 'line'
  /**
   * 탭 목록 정렬
   */
  orientation?: 'horizontal' | 'vertical'
  /**
   * 탭 목록이 전체 너비를 차지할지 여부
   */
  fullWidth?: boolean
  /**
   * 컨테이너 클래스명
   */
  containerClassName?: string
}

export interface UITabsListProps extends React.ComponentProps<typeof TabsList> {
  size?: 'sm' | 'default' | 'lg'
  variant?: 'default' | 'pills' | 'line'
  fullWidth?: boolean
}

export interface UITabsTriggerProps extends React.ComponentProps<typeof TabsTrigger> {
  size?: 'sm' | 'default' | 'lg'
  variant?: 'default' | 'pills' | 'line'
  icon?: React.ReactNode
}

export interface UITabsContentProps extends React.ComponentProps<typeof TabsContent> {}

const sizeVariants = {
  sm: {
    list: "h-10 p-1 gap-1.5",
    trigger: "h-8 px-3 py-1.5 text-xs"
  },
  default: {
    list: "h-12 p-1 gap-2",
    trigger: "h-10 px-4 py-2 text-sm"
  },
  lg: {
    list: "h-14 p-1 gap-2",
    trigger: "h-12 px-5 py-3 text-base"
  }
}

const variantStyles = {
  // default: shadcn 기본 스타일 유지 + 미세한 개선
  default: {
    list: "",  // 기본 tabs.tsx 스타일 그대로 사용
    trigger: [
      // 활성 상태에서만 미세한 강조 추가
      "data-[state=active]:shadow-sm",
      "data-[state=active]:ring-1 data-[state=active]:ring-primary/10",
      // 아이콘 색상: 활성 상태에서 primary로 강조
      "[&_svg]:text-current",
      "data-[state=active]:[&_svg]:text-primary"
    ].join(" ")
  },
  // pills: 둥근 pill 형태
  pills: {
    list: "bg-transparent gap-2 border-none ring-0 shadow-none",
    trigger: [
      "rounded-full border border-transparent",
      "hover:bg-muted/50",
      "data-[state=active]:bg-primary data-[state=active]:text-primary-foreground",
      "data-[state=active]:border-primary data-[state=active]:shadow-sm",
      // 아이콘 색상: 활성 상태에서 명시적으로 primary-foreground 적용
      "data-[state=active]:[&_svg]:text-primary-foreground"
    ].join(" ")
  },
  // line: 하단 라인 인디케이터 스타일
  line: {
    list: [
      "bg-transparent dark:bg-transparent",
      "border-0 border-b border-border ring-0 shadow-none rounded-none",
      "h-auto p-0 gap-6",
      "flex overflow-x-auto scrollbar-hide",
    ].join(" "),
    trigger: [
      // 기본 스타일 초기화 + flex-1 오버라이드
      "rounded-none border-0 bg-transparent shadow-none ring-0",
      "flex-initial flex-shrink-0",
      // 비활성 상태: muted 텍스트
      "text-muted-foreground",
      // hover: 텍스트 강조 + 미세한 하단 라인 프리뷰
      "hover:bg-transparent hover:border-0 hover:ring-0 hover:shadow-none",
      "hover:text-foreground/80",
      "hover:after:bg-muted-foreground/25",
      // active: 선명한 Teal 텍스트 + 볼드 라인
      "data-[state=active]:bg-transparent data-[state=active]:shadow-none",
      "data-[state=active]:ring-0 data-[state=active]:border-0",
      "data-[state=active]:text-[var(--tab-active,hsl(var(--primary)))] data-[state=active]:font-semibold",
      // 하단 라인 인디케이터 (after pseudo-element) + 트랜지션
      "relative after:absolute after:bottom-0 after:left-0 after:right-0 after:h-[2px]",
      "after:bg-transparent after:rounded-full",
      "after:transition-[background-color] after:duration-200 after:ease-out",
      "data-[state=active]:after:bg-[var(--tab-active,hsl(var(--primary)))]",
      // 하단 패딩 (인디케이터 공간 확보)
      "pb-3",
      // 아이콘 색상
      "[&_svg]:text-current",
      "data-[state=active]:[&_svg]:text-[var(--tab-active,hsl(var(--primary)))]"
    ].join(" ")
  }
}

/**
 * 탭 컴포넌트
 * 다양한 크기, 변형, 방향, 아이콘을 지원합니다.
 */
export const UITabs = React.forwardRef<
  React.ComponentRef<typeof Tabs>,
  UITabsProps
>(({
  className,
  items,
  size = "default",
  variant = "default",
  orientation = "horizontal",
  fullWidth = false,
  containerClassName,
  children,
  ...props
}, ref) => {
  if (items) {
    return (
      <UIBox className={cn(
        "w-full max-w-full min-w-0 flex-shrink-0",
        orientation === 'vertical' && "flex gap-4",
        containerClassName
      )}>
        <Tabs
          ref={ref}
          orientation={orientation}
          className={cn(
            orientation === 'vertical' && "flex-1",
            className
          )}
          {...props}
        >
          <UITabsList
            size={size}
            variant={variant}
            fullWidth={fullWidth}
            className={cn(
              orientation === 'vertical' && "flex-col h-auto w-48"
            )}
          >
            {items.map((item) => (
              <UITabsTrigger
                key={item.value}
                value={item.value}
                size={size}
                variant={variant}
                icon={item.icon}
                disabled={item.disabled}
                className={cn(
                  orientation === 'vertical' && "w-full max-w-full min-w-0 justify-start"
                )}
              >
                {item.label}
              </UITabsTrigger>
            ))}
          </UITabsList>

          {items.map((item) => (
            <UITabsContent key={item.value} value={item.value}>
              {item.content}
            </UITabsContent>
          ))}
        </Tabs>
      </UIBox>
    )
  }

  return (
    <Tabs
      ref={ref}
      orientation={orientation}
      className={cn(className)}
      {...props}
    >
      {children}
    </Tabs>
  )
})

export const UITabsList = React.forwardRef<
  React.ComponentRef<typeof TabsList>,
  UITabsListProps
>(({
  className,
  size = "default",
  variant = "default",
  fullWidth = false,
  ...props
}, ref) => (
  <TabsList
    ref={ref}
    className={cn(
      sizeVariants[size].list,
      variantStyles[variant].list,
      fullWidth && "w-full max-w-full min-w-0 flex-shrink-0",
      className
    )}
    {...props}
  />
))

export const UITabsTrigger = React.forwardRef<
  React.ComponentRef<typeof TabsTrigger>,
  UITabsTriggerProps
>(({
  className,
  size = "default",
  variant = "default",
  icon,
  children,
  ...props
}, ref) => (
  <TabsTrigger
    ref={ref}
    data-variant={variant}
    className={cn(
      sizeVariants[size].trigger,
      variantStyles[variant].trigger,
      icon && "gap-1.5",
      className
    )}
    {...props}
  >
    {icon && <UITypography as="span" variant="inline" color="inherit" className="flex-shrink-0">{icon}</UITypography>}
    {children}
  </TabsTrigger>
))

export const UITabsContent = React.forwardRef<
  React.ComponentRef<typeof TabsContent>,
  UITabsContentProps
>(({ className, ...props }, ref) => (
  <TabsContent
    ref={ref}
    className={cn(className)}
    {...props}
  />
))

// 특정 용도별 탭 컴포넌트들
/**
 * 설정 페이지용 탭 프리셋
 * Apple 글래스모피즘 스타일, 가로 방향, 전체 너비
 */
export const UISettingsTabs = React.forwardRef<
  React.ComponentRef<typeof UITabs>,
  Omit<UITabsProps, 'variant' | 'orientation'>
>(({ fullWidth = true, ...props }, ref) => (
  <UITabs
    ref={ref}
    variant="default"
    orientation="horizontal"
    fullWidth={fullWidth}
    {...props}
  />
))

export const UISidebarTabs = React.forwardRef<
  React.ComponentRef<typeof UITabs>,
  Omit<UITabsProps, 'variant' | 'orientation' | 'size'>
>(({ ...props }, ref) => (
  <UITabs
    ref={ref}
    variant="pills"
    orientation="vertical"
    size="sm"
    {...props}
  />
))

UITabs.displayName = "UITabs"
UITabsList.displayName = "UITabsList"
UITabsTrigger.displayName = "UITabsTrigger"
UITabsContent.displayName = "UITabsContent"
UISettingsTabs.displayName = "UISettingsTabs"
UISidebarTabs.displayName = "UISidebarTabs"